import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const initial = {
  name: "",
  email: "",
  contact: "",
  brand_name: "",
  requirements: "",
  timeline: "",
};

const TIMELINES = [
  { value: "in_a_week", label: "In a week" },
  { value: "in_a_month", label: "In a month" },
  { value: "in_3_months", label: "In 3 months" },
  { value: "in_6_months", label: "In 6 months" },
];

export default function CollaborationForm() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const required = ["name", "email", "contact", "brand_name", "requirements", "timeline"];
    if (required.some((k) => !form[k])) {
      toast.error("Please fill in all fields.");
      return;
    }
    try {
      setLoading(true);
      await api.post("/leads/collaboration", form);
      toast.success("Brief received. Nitin's team will revert with next steps.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again or message on WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="collab-form" className="space-y-7">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
        <Field label="Your name" testid="cb-name">
          <input
            data-testid="cb-input-name"
            name="name" value={form.name} onChange={onChange}
            placeholder="Your name"
            className="input-airy w-full bg-transparent border-0 border-b border-stone-300 py-3 text-lg focus:border-terracotta"
          />
        </Field>
        <Field label="Brand name" testid="cb-brand">
          <input
            data-testid="cb-input-brand"
            name="brand_name" value={form.brand_name} onChange={onChange}
            placeholder="e.g., Acme Co."
            className="input-airy w-full bg-transparent border-0 border-b border-stone-300 py-3 text-lg focus:border-terracotta"
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
        <Field label="Email" testid="cb-email">
          <input
            data-testid="cb-input-email"
            type="email" name="email" value={form.email} onChange={onChange}
            placeholder="you@brand.com"
            className="input-airy w-full bg-transparent border-0 border-b border-stone-300 py-3 text-lg focus:border-terracotta"
          />
        </Field>
        <Field label="WhatsApp / Phone" testid="cb-contact">
          <input
            data-testid="cb-input-contact"
            name="contact" value={form.contact} onChange={onChange}
            placeholder="+91 9XXXXXXXXX"
            className="input-airy w-full bg-transparent border-0 border-b border-stone-300 py-3 text-lg focus:border-terracotta"
          />
        </Field>
      </div>

      <Field label="What do you have in mind?" testid="cb-req">
        <textarea
          data-testid="cb-input-requirements"
          name="requirements" value={form.requirements} onChange={onChange}
          rows={4}
          placeholder="Campaign idea, deliverables, platforms..."
          className="input-airy w-full bg-transparent border-0 border-b border-stone-300 py-3 text-lg resize-none focus:border-terracotta"
        />
      </Field>

      <Field label="Timeline" testid="cb-timeline">
        <Select
          value={form.timeline}
          onValueChange={(v) => setForm({ ...form, timeline: v })}
        >
          <SelectTrigger
            data-testid="cb-input-timeline"
            className="w-full bg-transparent border-0 border-b border-stone-300 rounded-none py-3 h-auto text-lg focus:ring-0 focus:border-terracotta"
          >
            <SelectValue placeholder="How soon do you want to launch?" />
          </SelectTrigger>
          <SelectContent>
            {TIMELINES.map((t) => (
              <SelectItem key={t.value} value={t.value} data-testid={`cb-timeline-${t.value}`}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      <button
        type="submit"
        disabled={loading}
        data-testid="cb-submit"
        className="inline-flex items-center justify-center rounded-full bg-terracotta text-white px-8 py-4 font-semibold hover:bg-terracotta-deep transition-colors disabled:opacity-60"
      >
        {loading ? "Sending..." : "Submit Collaboration Brief"}
        <span aria-hidden className="ml-2">→</span>
      </button>
    </form>
  );
}

function Field({ label, testid, children }) {
  return (
    <label data-testid={`field-${testid}`} className="block">
      <span className="overline">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
